/* Copyright CloudLinux Zug GmbH 2010-2017 All Rights Reserved
 *
 * Licensed under CLOUD LINUX LICENSE AGREEMENT
 * http://cloudlinux.com/docs/LICENSE.TXT
 */

import { NgModule, ApplicationRef, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { removeNgStyles, createNewHosts, createInputTransfer } from '@angularclass/hmr';
import Raven = require('raven-js');
/*
 * Platform and Environment providers/directives/pipes
 */
import { ENV_PROVIDERS } from './environment';
import { ROUTES } from './app.routes';
// App is our top level component
import { App } from './app-component/app.component';
import { AppState, InternalStateType } from '../app/app.service';
import { ConfigRepo } from '../app/repository/config_repo';
import { XHR } from '../app/utils/xhr';
import { NotificationsService } from '../app/services/notifiaction_service';
import { UsersRepo } from '../app/repository/users_repo';
import { RavenTransport } from '../app/utils/raven_transport';
import { MockService } from '../app/services/mock';
import { UnavailableComponent } from '../app/unavailable.component';
import { UnavailableService } from '../app/services/unavailable-service';
import { TranslateLocaleHttpLoader } from '../app/utils/TranslateLocaleHttpLoader';
import { CagefsRepo } from '../app/repository/cagefs_repo';

import { AppListComponent } from './applist-component/applist.component';
import { ComponentModule } from '../app/utils/ui-elements';
import { ApplicationComponent } from './application-component/application.component';
import { AppService } from './app.service';
import { SelectorRepo } from '../app/repository/selector_repo';
import { EnviromentGrid } from '../app/utils/environment_grid';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CommonComponentModule } from '../common.components';
import { CommonPipes } from '../app/utils/pipes';
import { TranslateLocaleMissingHandler } from '../app/utils/TranslateLocaleMissingHandler';
import { MissingTranslationHandler, TranslateLoader,
    TranslateModule, TranslateParser } from '@ngx-translate/core';
import { TranslateLocaleParser } from '../app/utils/TranslateLocaleParser';
import { WindowRef } from '../app/services/window.service';
import { TooltipModule } from 'ngx-bootstrap';

const ENV = process.env.ENV;
// Application wide providers
const APP_PROVIDERS = [
    AppState,
    AppService
];

type StoreType = {
    state: InternalStateType,
    restoreInputValues: () => void,
    disposeOldHosts: () => void
};

class RavenErrorHandler implements ErrorHandler {

    handleError(err: any): void {
        Raven.captureException(err.originalError || err);
    }
}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateLocaleHttpLoader(http, 'i18n/');
}


/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
    bootstrap: [ App ],
    declarations: [
        App,
        AppListComponent,
        ApplicationComponent,

        EnviromentGrid,
    ],
    imports: [ // import Angular's modules
        BrowserModule,
        FormsModule,
        TooltipModule.forRoot(),
        BrowserAnimationsModule,
        BsDropdownModule.forRoot(),
        HttpClientModule,
        HttpClientXsrfModule.withOptions({
          cookieName: 'csrftoken',
          headerName: 'X-CSRFToken',
        }),
        RouterModule.forRoot(ROUTES, { useHash: true, preloadingStrategy: PreloadAllModules }),
        TranslateModule.forRoot({
           loader: {
               provide: TranslateLoader,
               useFactory: HttpLoaderFactory,
               deps: [HttpClient]
           },
           parser: {
               provide: TranslateParser,
               useClass: TranslateLocaleParser
           },
           missingTranslationHandler : {
               provide: MissingTranslationHandler,
               useClass: TranslateLocaleMissingHandler
           }
        }),
        CommonComponentModule,
        ComponentModule,
        CommonPipes,
    ],
    entryComponents: [UnavailableComponent],
    providers: [ // expose our Services and Providers into Angular's dependency injection
        ENV_PROVIDERS,
        APP_PROVIDERS,
        NotificationsService, // no need to declare this 4 providers in each component
        XHR,
        RavenTransport,
        WindowRef,
        UsersRepo,
        ConfigRepo,
        CagefsRepo,
        SelectorRepo,
        MockService,
        UnavailableService,
    ].concat(ENV === 'production' ? [{ provide: ErrorHandler, useClass: RavenErrorHandler }] :
      []) // Error Handler will block errors in development, don't use it there
})
export class AppModule {
    constructor(public appRef: ApplicationRef,
                public appState: AppState,
                private xhrTransport: RavenTransport
    ) {
        this.initSentry();
    }

    private initSentry() {
        if (ENV === 'production') {
            Raven.config('https://7c2380b5311d46129e484f9805d5d1c6@sentry.io/136479', {
                tags: {
                    env: ENV
                },
                transport: (options) => this.xhrTransport.post(options),
                ignoreErrors: [
                    /.*please install or reinstall cagefs.*/,
                    /.*CageFS is not initialized. Use "\/usr\/sbin\/cagefsctl --init" to initialize CageFS.*/,
                    /.*%(what)s set error for package=%(package)s%(error)s.*/,
                    /.*%(what)s set error for uid=%(uid)s%(error)s.*/,
                    /.*Server is not running, Use \'service lvestats start\' command to start it.*/,
                    /.*alt-php packages not found.*/,
                    /.*no such script \/usr\/sbin\/cagefsctl.*/,
                    /.*License is not valid.*/,
                    /.*Session expired.*/,
                ]
            }).install();
        }
    }

}




// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./node_modules/angular-router-loader/src?loader=system&genDir=compiled&aot=false!./src/selector/app.module.ts